import { useParams } from 'react-router-dom';
import { useDashboardPageState } from '../../../hooks/useDashboardPageState';
import { useMeetingNotes } from '../../../api/healthCoach/useMeetingNotes';
import { CareModel } from '../../../models/user';
import { PmStatus, ProviderType } from '../../../api/types';
import { hasFutureMeetingWithProviderType, hasMeetingStarted } from '../utils';
import { MeetingType } from '../../../models/meeting';

export const useSelectMeetingType = () => {
  const {
    state: { user },
  } = useDashboardPageState();
  const { patient } = useParams();
  const { meetingNotes, isInitialLoading, isLoading, isError } =
    useMeetingNotes(patient ?? '');

  const showCollaborativeCareMeetings =
    user?.care_model === CareModel.Collaborative;

  const initialCoachCall = meetingNotes?.find(
    (meeting) => meeting.meeting_type === MeetingType.InitialCoachCall
  );

  const initialConsultation = meetingNotes?.find(
    (meeting) => meeting.meeting_type === MeetingType.InitialConsultation
  );

  // In progress means the meeting started, but it is not finished yet
  // aka no pm_status has been set yet.
  // We check we do not have any pm_status because if it has been set,
  // it might be the user did not show up, so we ignore the time.
  const isInitialCoachCallInProgress =
    !initialCoachCall?.pm_status &&
    hasMeetingStarted(initialCoachCall?.meeting_date);

  const isInitialConsultationInProgress =
    !initialConsultation?.pm_status &&
    hasMeetingStarted(initialConsultation?.meeting_date);

  const hasInitialCoachCallOccurred =
    initialCoachCall?.pm_status === PmStatus.occurred;

  const hasInitialConsultationOccurred =
    initialConsultation?.pm_status === PmStatus.occurred;

  const nextCoachMeetingType =
    isInitialCoachCallInProgress || hasInitialCoachCallOccurred
      ? MeetingType.FollowUpSession
      : MeetingType.InitialCoachCall;

  const nextProviderMeetingType =
    isInitialConsultationInProgress || hasInitialConsultationOccurred
      ? MeetingType.FollowUpSession
      : MeetingType.InitialConsultation;

  const hasCoachCallScheduled = hasFutureMeetingWithProviderType(
    meetingNotes,
    ProviderType.Coach
  );

  const hasProviderCallScheduled = hasFutureMeetingWithProviderType(
    meetingNotes,
    ProviderType.Provider
  );

  return {
    hasCoachCallScheduled,
    hasProviderCallScheduled,
    isError: !user || isError,
    isLoading: isLoading || isInitialLoading,
    nextCoachMeetingType,
    nextProviderMeetingType,
    showCollaborativeCareMeetings,
    userId: user?.id,
  };
};
